import { withStyles } from '@material-ui/styles';
import React, { useRef, useContext } from 'react';
import styles from './add-edit-contact-modal.style';
import { Box } from '@material-ui/core';
import { Typography } from '@orijinworks/frontend-commons';
import Button from '@material-ui/core/Button';

// Create a context to share the hook instance
const ManageContactContext = React.createContext(null);

export const ManageContactProvider = ({ children, hookInstance }) => (
    <ManageContactContext.Provider value={hookInstance}>
        {children}
    </ManageContactContext.Provider>
);

const ImageDropArea = ({ classes }) => {
    // Use the shared hook instance from context
    const hookFromContext = useContext(ManageContactContext);
    
    // Destructure values from context
    const {
        selectedFile,
        previewUrl,
        isDragging,
        error,
        onDrop,
        onDragEnter,
        onDragLeave,
        onFileInputChange,
        clearSelectedFile
    } = hookFromContext;

    const fileInputRef = useRef(null);
    return (
        <Box
            className={classes.section}
        >
            <Typography
                className={classes.imgText}
                variant="body1">
                An image is crucial for verifying contact identity. It confirms that the person presenting the ID is the rightful owner.
            </Typography>
            {/* BEGIN: IMAGE DROP AREA */}
            {!selectedFile && (
                <Box
                    id="image-drop-area"
                    className={`${classes.imageDropArea} 
                    ${error || (hookFromContext.formik && hookFromContext.formik.touched.file && hookFromContext.formik.errors.file) ? classes.errorBox : ''}
                    ${isDragging ? classes.dragging : ''} ${selectedFile ? classes.hasFile : ''}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Only trigger if this is the outer container
                        if (e.currentTarget === e.target) {
                            onDragEnter();
                        }
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Only trigger if we're leaving the outer container
                        // Check if the related target is outside the drop area
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                            onDragLeave();
                        }
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDragLeave();
                        const files = e.dataTransfer.files;
                        if (files && files.length > 0) {
                            onDrop([files[0]]);
                        }
                    }}
                >
                    <Typography
                        variant='h6'>
                        Select a file or drag and drop here
                    </Typography>
                    <Typography
                        className={classes.dropAreaText}
                        variant="body1">
                        JPG, PNG or WebP, file size no more than 1 MB
                    </Typography>
                    <Box className={classes.fileSelector}>
                        <Button
                            variant="outlined"
                            color="primary"
                            className={classes.selectFileBtn}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => {
                                    e.persist(); // Persist the synthetic event
                                    onFileInputChange(e);
                                }}
                            />
                            Select File
                        </Button>
                    </Box>
                </Box>
            )}
            {selectedFile && (
                <Box className={classes.previewContainer}>
                    <Box margin="10px" width="180px" display="flex" alignItems="center" justifyContent="center">
                        <img
                            src={previewUrl}
                            alt="Preview"
                            className={classes.imagePreview}
                        />
                    </Box>
                    <Box>
                        <Typography variant="h6">
                            {selectedFile.name}
                        </Typography>
                        <Button
                            onClick={clearSelectedFile}
                            className={classes.removeBtn}
                            variant='outlined'>
                            Replace Image
                        </Button>
                    </Box>
                </Box>
            )}

            {(error || (hookFromContext.formik && hookFromContext.formik.touched.file && hookFromContext.formik.errors.file)) && (
                <Typography color="error" variant="body2" className={classes.errorText}>
                    {error || hookFromContext.formik.errors.file}
                </Typography>
            )}
            {/* END: IMAGE DROP AREA */}
        </Box>
    );
};

export default withStyles(styles)(ImageDropArea);
